/**
 * "How we measured", at `/measured` (owner, 2026-10-31): the study behind the
 * landing's savings card, as a page a person can read. The link under the
 * card used to open the Markdown report itself, which is right for an agent
 * and off-putting for a person: plain text on a dark ground.
 *
 * The figures are the same public aggregate the card computes from
 * (lib/savings.ts), and the three files beside it stay where they are for
 * anybody who wants the data itself: the JSON, a CSV of the two comparisons
 * and the Markdown report, which agents read. web/test/measured.test.ts keeps
 * the CSV equal to what `evidenceCsv` makes from the JSON, so the
 * spreadsheet cannot drift from the page.
 */
import { savingsEvidence, type SavingsBenchmark } from './savings';

/** Where the page lives. */
export const MEASURED_PATH = '/measured';

/** Whether this address is the page; a trailing slash is the same page. */
export function isMeasuredPath(pathname: string): boolean {
  return /^\/measured\/?$/.test(pathname);
}

const EDITION = savingsEvidence.edition;

/** The downloads, each a static file in web/public/evidence/. */
export const EVIDENCE_FILES = {
  json: `/evidence/${EDITION}.json`,
  csv: `/evidence/${EDITION}.csv`,
  md: `/evidence/${EDITION}.md`,
} as const;

/** The two comparisons, in the order the page shows them. */
export const BENCHMARKS: readonly SavingsBenchmark[] = ['model', 'context'];

/** One comparison, said in dollars and shares rather than micro-dollars. */
export function benchmarkView(kind: SavingsBenchmark) {
  const b = savingsEvidence.benchmarks[kind];
  return {
    before: b.beforeMicroUsd / 1e6,
    after: b.afterMicroUsd / 1e6,
    /** The share of the cost that went, 0.799 for 79.9%. */
    saved: 1 - b.afterMicroUsd / b.beforeMicroUsd,
    afterShare: b.afterMicroUsd / b.beforeMicroUsd,
    beforeCorrect: b.beforeCorrect,
    afterCorrect: b.afterCorrect,
    targets: b.targets,
    beforeAttempts: b.beforeAttempts,
    afterAttempts: b.afterAttempts,
    beforeSeconds: b.beforeMedianSeconds,
    afterSeconds: b.afterMedianSeconds,
    qualityPreserved: b.qualityPreserved,
    regressions: b.qualityRegressions,
    range: b.descriptiveFamilyBootstrap as [number, number],
  };
}

const CSV_COLUMNS = [
  'comparison',
  'before_cost_usd',
  'after_cost_usd',
  'cost_reduction_pct',
  'family_bootstrap_95_low_pct',
  'family_bootstrap_95_high_pct',
  'targets',
  'before_correct_and_compliant',
  'after_correct_and_compliant',
  'before_attempts',
  'after_attempts',
  'before_median_seconds',
  'after_median_seconds',
  'no_task_regression',
  'task_regressions',
  'price_book',
] as const;

const pct = (share: number) => (share * 100).toFixed(1);

/**
 * The two comparisons as a CSV a spreadsheet opens: one row each, costs in
 * dollars to the micro-dollar the audit recorded, and nothing the JSON does
 * not already hold.
 */
export function evidenceCsv(): string {
  const rows = BENCHMARKS.map((kind) => {
    const b = savingsEvidence.benchmarks[kind];
    const v = benchmarkView(kind);
    return [
      kind === 'model' ? 'model routing' : 'focused context',
      v.before.toFixed(6),
      v.after.toFixed(6),
      pct(v.saved),
      pct(v.range[0]),
      pct(v.range[1]),
      b.targets,
      b.beforeCorrect,
      b.afterCorrect,
      b.beforeAttempts,
      b.afterAttempts,
      b.beforeMedianSeconds.toFixed(1),
      b.afterMedianSeconds.toFixed(1),
      b.qualityPreserved ? 'yes' : 'no',
      b.qualityRegressions.join(' '),
      savingsEvidence.priceBook,
    ].join(',');
  });
  return [CSV_COLUMNS.join(','), ...rows].join('\n') + '\n';
}
